// Themes from the app's assets/json/general/quick/twenty_challenge/<lang>.json.
// Every language file has the same themes in the same order; item lists run
// index-parallel to en.json (the artwork is keyed by item index).

export const CONTENT = '/content/twenty';
export const GAME_ID = 'game_twenty_challenge';
export const ITEMS_PER_THEME = 10;
export const BUDGET = 20;

export interface ThemeItem {
  name: string;
  kind?: string;
  order?: number;
}

export interface Theme {
  id: string;
  category: string;
  theme: string; // "Build the best pizza"
  title: string; // "Pizza"
  base?: string;
  model?: string;
  adult?: boolean;
  items: (ThemeItem | string)[];
}

export interface Manifest {
  languages: string[];
  visuals: string[];
  previews: string[];
}

/// Pack order in the picker, and the app string keys of name and blurb.
export const PACKS: { key: string; name: string; desc: string; adult?: boolean }[] = [
  { key: 'food', name: 'twentyChallengeFood', desc: 'twentyChallengeFoodDesc' },
  { key: 'places', name: 'twentyChallengePlaces', desc: 'twentyChallengePlacesDesc' },
  { key: 'dream-life', name: 'twentyChallengeDreamLife', desc: 'twentyChallengeDreamLifeDesc' },
  { key: 'fantasy', name: 'twentyChallengeFantasy', desc: 'twentyChallengeFantasyDesc' },
  { key: 'sports-gaming', name: 'twentyChallengeSports', desc: 'twentyChallengeSportsDesc' },
  { key: 'superheroes', name: 'twentyChallengeSuperheroes', desc: 'twentyChallengeSuperheroesDesc' },
  { key: 'movies-tv', name: 'twentyChallengeMoviesTv', desc: 'twentyChallengeMoviesTvDesc' },
  { key: 'party', name: 'twentyChallengeParty', desc: 'twentyChallengePartyDesc' },
  { key: 'naughty', name: 'twentyChallengeNaughty', desc: 'twentyChallengeNaughtyDesc', adult: true },
];

export const ADULT_PACKS = new Set(PACKS.filter((p) => p.adult).map((p) => p.key));

export const isAdultTheme = (t: Theme) => t.adult === true || ADULT_PACKS.has(t.category);

export const itemName = (it: ThemeItem | string) => (typeof it === 'string' ? it : it.name);

let manifest: Promise<Manifest> | null = null;

export function loadManifest(): Promise<Manifest> {
  manifest ??= fetch(`${CONTENT}/index.json`).then((r) => {
    if (!r.ok) throw new Error('manifest');
    return r.json() as Promise<Manifest>;
  });
  return manifest;
}

const byLang = new Map<string, Promise<Theme[]>>();

export function loadThemes(lang: string): Promise<Theme[]> {
  let p = byLang.get(lang);
  if (!p) {
    p = fetch(`${CONTENT}/themes/${lang}.json`).then((r) => {
      if (!r.ok) throw new Error(`themes ${lang}`);
      return r.json() as Promise<Theme[]>;
    });
    byLang.set(lang, p);
    p.catch(() => byLang.delete(lang));
  }
  return p;
}

export interface RoundSetup {
  theme: Theme;
  /// Item names in game order and, parallel, each item's index in the
  /// theme's `items` (what the artwork is keyed by).
  items: string[];
  sources: number[];
}

/// Picks up to [ITEMS_PER_THEME] items in random order, skipping hidden ones.
export function dealItems(theme: Theme, hidden: Set<number>, rnd = Math.random): RoundSetup {
  const order: number[] = [];
  theme.items.forEach((_, i) => {
    if (!hidden.has(i)) order.push(i);
  });
  for (let i = order.length - 1; i > 0; i--) {
    const j = Math.floor(rnd() * (i + 1));
    [order[i], order[j]] = [order[j], order[i]];
  }
  const sources = order.slice(0, Math.min(ITEMS_PER_THEME, order.length));
  return { theme, sources, items: sources.map((i) => itemName(theme.items[i])) };
}

/// A random theme not yet seen this session; Skip walks through every theme
/// before one comes back (like the app).
export function pickTheme(pool: Theme[], seen: Set<string>, currentId?: string, rnd = Math.random): Theme | null {
  if (pool.length === 0) return null;
  let fresh = pool.filter((t) => !seen.has(t.id));
  if (fresh.length === 0) {
    seen.clear();
    fresh = pool.filter((t) => t.id !== currentId || pool.length === 1);
  }
  const t = fresh[Math.floor(rnd() * fresh.length)];
  seen.add(t.id);
  return t;
}

/// [count] different themes for a chat vote.
export function pickThemes(pool: Theme[], seen: Set<string>, count: number, rnd = Math.random): Theme[] {
  const out: Theme[] = [];
  for (let i = 0; i < count; i++) {
    const t = pickTheme(
      pool.filter((p) => !out.includes(p)),
      seen,
      undefined,
      rnd,
    );
    if (!t) break;
    out.push(t);
  }
  return out;
}
